import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { TrendingUp, Clock, Trophy, Plus, Bell } from 'lucide-react';
import { useWebSocket } from '../hooks';

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const [marketUpdates, setMarketUpdates] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [liveNotificationCount, setLiveNotificationCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState(null);
  
  const handleWebSocketMessage = useCallback((data) => {
    console.log('WebSocket message received:', data.type);
    
    switch (data.type) {
      case 'price_update':
        setMarketUpdates(prev => ({
          ...prev,
          [data.market_id]: {
            ...prev[data.market_id],
            lastPriceUpdate: data
          }
        }));
        toast.info(`Price updated on ${data.data?.market_title || 'a market'}`, {
          duration: 2000,
          icon: <TrendingUp className="h-4 w-4 text-emerald-500" />
        });
        break;
        
      case 'market_closed':
        toast.warning(`Market closed: ${data.title}`, {
          icon: <Clock className="h-4 w-4" />,
          duration: 4000
        });
        setMarketUpdates(prev => ({
          ...prev,
          [data.market_id]: {
            ...prev[data.market_id],
            status: 'closed',
            closedAt: data.timestamp
          }
        }));
        break;
        
      case 'market_settled':
        toast.success(`Market settled: ${data.title} → ${data.winning_outcome}`, {
          icon: <Trophy className="h-4 w-4" />,
          duration: 5000
        });
        setMarketUpdates(prev => ({
          ...prev,
          [data.market_id]: {
            ...prev[data.market_id],
            status: 'settled',
            winningOutcome: data.winning_outcome,
            settledAt: data.timestamp
          }
        }));
        break;
        
      case 'market_created':
        toast.info(`New market: ${data.title}`, {
          icon: <Plus className="h-4 w-4 text-blue-500" />,
          duration: 4000
        });
        break;
      
      case 'notification':
        setLiveNotificationCount(prev => prev + 1);
        setLatestNotification(data.notification);
        toast(data.notification.message, {
          description: data.notification.title,
          icon: <Bell className="h-4 w-4 text-blue-500" />,
          duration: 5000
        });
        break;
      
      case 'auth_success':
        setLiveNotificationCount(data.unread_count || 0);
        console.log('WebSocket auth success, unread count:', data.unread_count);
        break;

      case 'wallet_changed':
        // Multi-Tab-Sync: Saldo aktualisieren ohne zusaetzlichen
        // HTTP-Roundtrip. AuthContext sitzt eine Ebene ueber dem
        // WebSocketProvider, also Bruecke via window-CustomEvent
        // (gleiches Muster wie auth:logout).
        try {
          window.dispatchEvent(new CustomEvent('wallet:changed', {
            detail: {
              balance: Number(data.new_balance),
              reason: data.reason,
              timestamp: data.timestamp,
            },
          }));
        } catch { /* defensive */ }
        break;

      case 'pong':
        break;
        
      default:
        break;
    }
  }, []);
  
  const { isConnected, subscribe, unsubscribe, authenticateUser } = useWebSocket(handleWebSocketMessage);
  
  useEffect(() => {
    setWsConnected(isConnected);
  }, [isConnected]);

  const resetNotificationCount = useCallback((newCount) => {
    setLiveNotificationCount(newCount);
  }, []);

  return (
    <WebSocketContext.Provider value={{ 
      wsConnected, 
      marketUpdates, 
      subscribeToMarket: subscribe, 
      unsubscribeFromMarket: unsubscribe,
      authenticateUser,
      liveNotificationCount,
      latestNotification,
      resetNotificationCount
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
